package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.Pin
import kotlinx.browser.window
import kotlin.js.json
import kotlin.js.Date

/**
 * SharedBoardManager handles the shared board state in Firebase.
 * 
 * This replaces the event-based state sync with a simple shared state model:
 * - All clients read/write to the same Firebase path
 * - Board state IS the source of truth (no reconstruction needed)
 * - Undo is just deleting a pin entry
 * 
 * Firebase Structure:
 * games/{roomId}/
 *   settings/
 *     maxUndosPerHand: 1
 *   meta/
 *     currentPlayerId, gamePhase, turnNumber, undosUsedThisHand, etc.
 *   players/{playerId}/
 *     name, score, isHost, hand[]
 *   board/pins/{row}_{col}/
 *     color, isDress, isJoker, isReversed, playerId, moveIndex
 */
object SharedBoardManager {
    
    // Firebase database reference (set from Main.kt)
    var database: dynamic = null

    /** True when running inside a sandboxed third-party iframe (Playgama, itch.io, etc.) */
    private val isThirdPartyIframe: Boolean get() {
        val isInIframe = js("window.self !== window.top") as Boolean
        val isOnOwnHosting = js("window.location.hostname.indexOf('pinpoint.click') !== -1 || window.location.hostname === 'localhost'") as Boolean
        return isInIframe && !isOnOwnHosting
    }

    /** Returns the Firebase REST base URL appropriate for the current environment. */
    private fun getRestBaseUrl(): String {
        val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.detectEnvironment() ==
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
        return if (isDiscord) "/firebasedatabase" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
    }

    /** True when REST must be used (Discord, PlayGama, or sandboxed iframe) */
    private val useRest: Boolean get() =
        isThirdPartyIframe || 
        co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.detectEnvironment() ==
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD ||
        co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.detectEnvironment() ==
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.PLAYGAMA
    
    // Current game state
    private var currentRoomId: String? = null
    private val currentPlayerId: String? 
        get() = FirebaseRoomManager.currentPlayerId
    private var isHost: Boolean = false
    
    // Firebase listeners
    private var boardListener: dynamic = null
    private var metaListener: dynamic = null
    private var playersListener: dynamic = null
    private var settingsListener: dynamic = null
    
    // Callbacks for state changes
    var onBoardChanged: ((Map<String, SharedPin>) -> Unit)? = null
    var onMetaChanged: ((GameMeta) -> Unit)? = null
    var onPlayersChanged: ((Map<String, SharedPlayer>) -> Unit)? = null
    var onSettingsChanged: ((GameSettings) -> Unit)? = null
    var onError: ((String) -> Unit)? = null
    
    // Move counter for this game
    private var nextMoveIndex: Int = 0
    
    // Current settings (cached)
    private var currentSettings: GameSettings = GameSettings()
    
    // Current players (cached)
    private var cachedPlayers: Map<String, SharedPlayer> = emptyMap()
    
    // ========================================
    // Data Classes
    // ========================================
    
    /**
     * Data class for a pin in the shared board state.
     * Simple JSON-compatible structure for cross-platform use.
     */
    data class SharedPin(
        val color: String,           // PinColor name: RED, GREEN, PURPLE, YELLOW, BLACK (NO BLUE!)
        val isDress: Boolean = false,
        val isJoker: Boolean = false,
        val isReversed: Boolean = false,
        val originalColor: String? = null,  // For jokers: the color it counts as for scoring
        val handColor: String? = null,      // For jokers: the original hand color (for undo)
        val playerId: String? = null,       // Who placed this (null for dress pins)
        val moveIndex: Int = -1             // Order placed (-1 for dress pins)
    )
    
    /**
     * Data class for game metadata.
     */
    data class GameMeta(
        val currentPlayerId: String,
        val currentPlayerIndex: Int = 0,
        val gamePhase: String = "PLAYING",  // PLAYING, PAUSED, FINISHED
        val turnNumber: Int = 1,
        val handCount: Int = 0,
        val undosUsedThisHand: Int = 0,
        val lastPinScore: Int = 0,  // Score from the last pin placed (synced for all players)
        val currentHandScore: Int = 0,  // Accumulated score for current hand (synced for all players)
        val scoringPositions: List<String> = emptyList(),  // Positions that contributed to last score (for white rings)
        val pauseEndTime: Long? = null,  // When the pause should end (for fallback timer)
        val pauseStartedBy: String? = null,  // Who started the pause
        val lastUpdated: Long = 0,
        val lastUpdatedBy: String? = null
    )
    
    /**
     * Data class for a player in the shared state.
     */
    data class SharedPlayer(
        val name: String,
        val score: Int = 0,
        val isHost: Boolean = false,
        val isAI: Boolean = false,  // Added: indicates if this is an AI player
        val aiDifficulty: String? = null,  // Added: AI difficulty level (EASY, MEDIUM, HARD, EXPERT)
        val hand: List<String> = emptyList(),  // List of PinColor names
        val jokersUsed: Int = 0  // Number of jokers used this game (max 4)
    )
    
    /**
     * Data class for game settings.
     */
    data class GameSettings(
        val maxUndosPerHand: Int = 1  // Default: 1, Range: 0-4
    )
    
    // ========================================
    // Initialization
    // ========================================
    
    /**
     * Initialize the shared board manager for a game.
     */
    fun initialize(roomId: String, playerId: String, isHost: Boolean) {
        console.log("SharedBoardManager: 🚀 Initializing for room=$roomId, player=$playerId, isHost=$isHost")
        console.log("SharedBoardManager: database is ${if (database != null) "SET" else "NULL"}")
        
        this.currentRoomId = roomId
        // We no longer set currentPlayerId here as it's a getter from FirebaseRoomManager
        this.isHost = isHost
        this.nextMoveIndex = 0
        this.currentSettings = GameSettings()
        
        // Monitor Firebase connection status
        monitorConnectionStatus()
        
        // Start listening to Firebase or use REST polling for Discord
        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST Sentinel for Discord (WebSockets blocked)")
            startSentinelViaRest(roomId)
        } else {
            console.log("SharedBoardManager: Setting up Firebase listeners...")
            subscribeToBoard(roomId)
            subscribeToMeta(roomId)
            subscribeToPlayers(roomId)
            subscribeToSettings(roomId)
            console.log("SharedBoardManager: Firebase listeners setup complete")
        }
    }
    
    // Connection monitoring
    private var connectionListener: dynamic = null
    private var isConnected: Boolean = false
    var onConnectionChanged: ((Boolean) -> Unit)? = null
    
    /**
     * Monitor Firebase connection status and log disconnections.
     */
    private fun monitorConnectionStatus() {
        val db = database ?: return
        
        try {
            val connectedRef = db.ref(".info/connected")
            connectionListener = connectedRef.on("value", { snapshot: dynamic ->
                val connected = snapshot.`val`() as? Boolean ?: false
                if (connected != isConnected) {
                    isConnected = connected
                    if (connected) {
                        console.log("🟢 SharedBoardManager: Firebase CONNECTED")
                    } else {
                        console.warn("🔴 SharedBoardManager: Firebase DISCONNECTED")
                    }
                    onConnectionChanged?.invoke(connected)
                }
            })
            console.log("SharedBoardManager: Connection monitoring started")
        } catch (e: Exception) {
            console.error("SharedBoardManager: Failed to monitor connection: ${e.message}")
        }
    }
    
    /**
     * Check if Firebase is currently connected.
     */
    fun isFirebaseConnected(): Boolean = isConnected
    
    /**
     * Clean up listeners when leaving a game.
     */
    fun cleanup() {
        console.log("SharedBoardManager: Cleaning up")
        
        val roomId = currentRoomId ?: return
        
        // Remove Firebase listeners
        try {
            val db = database
            if (db != null) {
                val boardRef = db.ref("games/$roomId/board/pins")
                val metaRef = db.ref("games/$roomId/meta")
                val playersRef = db.ref("games/$roomId/players")
                val settingsRef = db.ref("games/$roomId/settings")
                
                if (boardListener != null) {
                    boardRef.off("value", boardListener)
                }
                if (metaListener != null) {
                    metaRef.off("value", metaListener)
                }
                if (playersListener != null) {
                    playersRef.off("value", playersListener)
                }
                if (settingsListener != null) {
                    settingsRef.off("value", settingsListener)
                }
                
                // Also remove connection listener
                if (connectionListener != null) {
                    val connectedRef = db.ref(".info/connected")
                    connectedRef.off("value", connectionListener)
                    connectionListener = null
                }
            }
        } catch (e: Exception) {
            console.error("SharedBoardManager: Error cleaning up listeners: ${e.message}")
        }
        
        sentinelIntervalId?.let { kotlinx.browser.window.clearInterval(it) }
        sentinelIntervalId = null
        boardListener = null
        metaListener = null
        playersListener = null
        settingsListener = null
        currentRoomId = null
        isHost = false
        isConnected = false
    }

    private var sentinelIntervalId: Int? = null
    
    // JSON tracking to avoid redundant updates during polling
    private var lastBoardJson: String = ""
    private var lastMetaJson: String = ""
    private var lastPlayersJson: String = ""
    private var lastSettingsJson: String = ""
    
    private fun pollSentinel(roomId: String) {
        FirebaseRoomManager.getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            val fetchUrl = "${getRestBaseUrl()}/games/$roomId.json$authParam"
            
            kotlinx.browser.window.fetch(fetchUrl, js("({ method: 'GET', cache: 'no-store' })"))
                .then { response ->
                    if (response.ok) {
                        response.json().then { data ->
                            // data may be null if game node doesn't exist yet
                            processRestSnapshot(data)
                        }
                    } else {
                        val status = response.status as Int
                        if (status == 401 || status == 403) {
                            console.error("SharedBoardManager: 🔒 Sentinel REST AUTH FAILED ($status) - Firebase rules may require auth. Token was: ${if (token != null) "present" else "null"}")
                        } else {
                            console.warn("SharedBoardManager: 🔴 Sentinel REST failed: $status")
                        }
                    }
                }
                .catch { error ->
                    console.error("SharedBoardManager: 🔴 Sentinel REST error: $error")
                }
        }
    }
    
    private fun startSentinelViaRest(roomId: String) {
        sentinelIntervalId?.let { kotlinx.browser.window.clearInterval(it) }
        
        // Do an immediate first fetch so the board doesn't wait 1.5s
        pollSentinel(roomId)
        
        // Then poll every 1.5 seconds
        sentinelIntervalId = kotlinx.browser.window.setInterval({
            pollSentinel(roomId)
        }, 1500)
    }
    
    private fun processRestSnapshot(data: dynamic?) {
        try {
            // CRITICAL: Handle null data gracefully (happens when game node doesn't exist yet on PlayGama)
            if (data == null) {
                console.log("SharedBoardManager: 🟡 REST snapshot is null - game not yet initialized")
                // Clear board and metadata but don't crash
                if (lastBoardJson != "{}") {
                    lastBoardJson = "{}"
                    onBoardChanged?.invoke(emptyMap())
                }
                if (lastMetaJson != "{}") {
                    lastMetaJson = "{}"
                    // Don't invoke callback with null - just clear the tracking
                }
                if (lastPlayersJson != "{}") {
                    lastPlayersJson = "{}"
                    onPlayersChanged?.invoke(emptyMap())
                }
                if (lastSettingsJson != "{}") {
                    lastSettingsJson = "{}"
                }
                return
            }
            
            // 1. Process Board (data.board.pins)
            val boardData = data.board?.pins
            val currentBoardJson = try { JSON.stringify(boardData ?: "{}") } catch (e: Exception) { "" }
            
            if (boardData != null && currentBoardJson != lastBoardJson) {
                lastBoardJson = currentBoardJson
                val pins = mutableMapOf<String, SharedPin>()
                val keys = js("Object.keys")(boardData) as Array<String>
                for (key in keys) {
                    val pinData = boardData[key]
                    if (pinData != null) {
                        pins[key] = SharedPin(
                            color = (pinData.color as? String) ?: "RED",
                            isDress = (pinData.isDress as? Boolean) ?: false,
                            isJoker = (pinData.isJoker as? Boolean) ?: false,
                            isReversed = (pinData.isReversed as? Boolean) ?: false,
                            originalColor = pinData.originalColor as? String,
                            handColor = pinData.handColor as? String,
                            playerId = pinData.playerId as? String,
                            moveIndex = (pinData.moveIndex as? Number)?.toInt() ?: -1
                        )
                    }
                }
                
                val maxIndex = pins.values.maxOfOrNull { it.moveIndex } ?: -1
                if (maxIndex + 1 > nextMoveIndex) {
                    nextMoveIndex = maxIndex + 1
                }
                
                onBoardChanged?.invoke(pins)
            } else if (boardData == null && lastBoardJson != "{}") {
                // Also fires when lastBoardJson is still "" (initial state) to signal board is empty/not yet written
                lastBoardJson = "{}"
                onBoardChanged?.invoke(emptyMap())
            }
            
            // 2. Process Meta (data.meta) - safely access with null check
            val metaData = data?.meta
            val currentMetaJson = try { JSON.stringify(metaData ?: "{}") } catch (e: Exception) { "" }
            
            if (metaData != null && currentMetaJson != lastMetaJson) {
                lastMetaJson = currentMetaJson
                val meta = GameMeta(
                    currentPlayerId = (metaData.currentPlayerId as? String) ?: "",
                    currentPlayerIndex = (metaData.currentPlayerIndex as? Number)?.toInt() ?: 0,
                    gamePhase = (metaData.gamePhase as? String) ?: "PLAYING",
                    turnNumber = (metaData.turnNumber as? Number)?.toInt() ?: 1,
                    handCount = (metaData.handCount as? Number)?.toInt() ?: 0,
                    undosUsedThisHand = (metaData.undosUsedThisHand as? Number)?.toInt() ?: 0,
                    lastPinScore = (metaData.lastPinScore as? Number)?.toInt() ?: 0,
                    currentHandScore = (metaData.currentHandScore as? Number)?.toInt() ?: 0,
                    pauseEndTime = (metaData.pauseEndTime as? Number)?.toLong(),
                    pauseStartedBy = metaData.pauseStartedBy as? String,
                    lastUpdated = (metaData.lastUpdated as? Number)?.toLong() ?: 0,
                    lastUpdatedBy = metaData.lastUpdatedBy as? String
                )
                
                val rawPositions = metaData.scoringPositions
                val rawType = js("typeof rawPositions") as String
                
                val scoringPositions: List<String> = when {
                    rawPositions == null || rawPositions == undefined -> emptyList()
                    rawType == "string" -> {
                        val str = rawPositions.toString()
                        if (str.trim().isEmpty()) emptyList()
                        else str.split(",").map { it.trim() }.filter { it.isNotEmpty() }
                    }
                    js("Array.isArray(rawPositions)") as Boolean -> {
                        try {
                            val list = mutableListOf<String>()
                            val length = rawPositions.length as Int
                            for (i in 0 until length) {
                                val item = rawPositions[i]
                                if (item != null) list.add(item.toString())
                            }
                            list
                        } catch (e: Exception) { emptyList() }
                    }
                    rawType == "object" -> {
                        try {
                            val list = mutableListOf<String>()
                            val keys = js("Object.keys(rawPositions)") as Array<String>
                            for (key in keys) {
                                val item = rawPositions[key]
                                if (item != null) list.add(item.toString())
                            }
                            list
                        } catch (e: Exception) { emptyList() }
                    }
                    else -> emptyList()
                }
                
                val finalMeta = meta.copy(scoringPositions = scoringPositions)
                onMetaChanged?.invoke(finalMeta)
            }
            
            // 3. Process Players (data.players)
            val playersData = data?.players
            val currentPlayersJson = try { JSON.stringify(playersData ?: "{}") } catch (e: Exception) { "" }
            
            if (playersData != null && currentPlayersJson != lastPlayersJson) {
                lastPlayersJson = currentPlayersJson
                val playerMap = mutableMapOf<String, SharedPlayer>()
                val keys = js("Object.keys")(playersData) as Array<String>
                for (key in keys) {
                    val pData = playersData[key]
                    if (pData != null) {
                        val handList = mutableListOf<String>()
                        if (pData.hand != null) {
                            val length = (pData.hand.length as? Number)?.toInt() ?: 0
                            for (i in 0 until length) {
                                val color = pData.hand[i] as? String
                                if (color != null) handList.add(color)
                            }
                        }
                        
                        playerMap[key] = SharedPlayer(
                            name = (pData.name as? String) ?: "Unknown",
                            score = (pData.score as? Number)?.toInt() ?: 0,
                            isHost = (pData.isHost as? Boolean) ?: false,
                            isAI = (pData.isAI as? Boolean) ?: false,
                            aiDifficulty = pData.aiDifficulty as? String,
                            hand = handList,
                            jokersUsed = (pData.jokersUsed as? Number)?.toInt() ?: 0
                        )
                    }
                }
                
                cachedPlayers = playerMap
                onPlayersChanged?.invoke(playerMap)
            }
            
            // 4. Process Settings (data.settings)
            val settingsData = data?.settings
            if (settingsData != null) {
                val maxUndos = (settingsData.maxUndosPerHand as? Number)?.toInt() ?: 1
                currentSettings = GameSettings(maxUndosPerHand = maxUndos)
            }
            
        } catch (e: Exception) {
            console.error("SharedBoardManager: 🔴 Error processing REST snapshot: ${e.message}")
        }
    }

    
    // ========================================
    // Host: Game Setup
    // ========================================
    
    /**
     * Player info for game initialization
     */
    data class PlayerInitInfo(
        val id: String,
        val name: String,
        val isAI: Boolean = false,
        val aiDifficulty: String? = null
    )

    /**
     * Initialize a new game (HOST ONLY).
     * Creates dress pins and sets up initial game state.
     */
    fun initializeGame(
        roomId: String,
        players: List<PlayerInitInfo>,  // List of player info including AI status
        hostPlayerId: String,
        settings: GameSettings = GameSettings(),
        onComplete: (Boolean, String?) -> Unit
    ) {
        console.log("SharedBoardManager: 🎮 Initializing new game with maxUndosPerHand=${settings.maxUndosPerHand}")
        console.log("SharedBoardManager: Players: ${players.map { "${it.id}:${it.name} (isAI=${it.isAI})" }}")
        
        // CRITICAL: In PlayGama/Discord, database is null - that's OK, we use REST
        val db = database
        if (db == null) {
            console.log("SharedBoardManager: ℹ️ Database is null - will use REST-only initialization (OK for PlayGama/Discord)")
        }
        
        // Generate random dress pin colors
        val dressColors = listOf("RED", "GREEN", "PURPLE", "YELLOW").shuffled()
        val dressPinPositions = listOf("3_3", "3_6", "6_3", "6_6")
        console.log("SharedBoardManager: Dress pins: ${dressPinPositions.zip(dressColors)}")
        
        // Build the initial board with dress pins
        val boardPins = js("{}")
        dressPinPositions.forEachIndexed { index, pos ->
            boardPins[pos] = json(
                "color" to dressColors[index],
                "isDress" to true,
                "isJoker" to false,
                "isReversed" to false,
                "originalColor" to null,
                "playerId" to null,
                "moveIndex" to -1
            )
        }
        
        // Build players data with initial hands
        val playersData = js("{}")
        // Hand colors: one of each (RED, GREEN, PURPLE, YELLOW) - no BLACK, no duplicates
        val handColors = listOf("RED", "GREEN", "PURPLE", "YELLOW")
        
        players.forEachIndexed { index, playerInfo ->
            val playerId = playerInfo.id
            val playerName = playerInfo.name
            // Deal one of each color, shuffled
            val hand = handColors.shuffled()
            console.log("SharedBoardManager: Player $playerId ($playerName, isAI=${playerInfo.isAI}) hand: $hand")
            
            // Create JS array for Firebase compatibility
            val handArray = js("[]")
            hand.forEachIndexed { i, color ->
                handArray[i] = color
            }
            
            playersData[playerId] = json(
                "name" to playerName,
                "score" to 0,
                "isHost" to (playerId == hostPlayerId),
                "isAI" to playerInfo.isAI,
                "aiDifficulty" to playerInfo.aiDifficulty,
                "hand" to handArray,
                "jokersUsed" to 0
            )
        }
        
        // Build meta data
        // Ensure host is always the first player (per user requirement)
        val firstPlayerId = hostPlayerId
        console.log("SharedBoardManager: First player (starting): $firstPlayerId (HOST)")
        
        // Find index of host in players list to set correct initial index
        val firstPlayerIndex = players.indexOfFirst { it.id == hostPlayerId }.takeIf { it != -1 } ?: 0
        
        val metaData = json(
            "currentPlayerId" to firstPlayerId,
            "currentPlayerIndex" to firstPlayerIndex,
            "gamePhase" to "PLAYING",
            "turnNumber" to 1,
            "handCount" to 0,
            "lastUpdated" to Date.now(),
            "lastUpdatedBy" to hostPlayerId
        )
        
        // Build settings data
        val settingsData = json(
            "maxUndosPerHand" to settings.maxUndosPerHand
        )
        
        // Write everything to Firebase
        console.log("SharedBoardManager: 📤 Writing game state to games/$roomId")
        val boardObject = json("pins" to boardPins)
        val updates = json(
            "board" to boardObject,
            "players" to playersData,
            "meta" to metaData,
            "settings" to settingsData
        )
        
        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for game initialization")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val initUrl = "${getRestBaseUrl()}/games/$roomId.json$authParam"
                
                kotlinx.browser.window.fetch(initUrl, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(updates)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Game initialized successfully via REST")
                            nextMoveIndex = 0
                            currentSettings = settings
                            onComplete(true, null)
                        } else {
                            console.error("SharedBoardManager: ❌ REST init failed: ${response.status}")
                            onComplete(false, "REST init failed")
                        }
                    }
                    .catch { error ->
                        console.error("SharedBoardManager: ❌ REST init error: $error")
                        onComplete(false, error.toString())
                    }
            }
        } else {
            val gameRef = db.ref("games/$roomId")
            gameRef.update(updates)
                .then {
                    console.log("SharedBoardManager: ✅ Game initialized successfully")
                    nextMoveIndex = 0
                    currentSettings = settings
                    onComplete(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to initialize game: ${error.message}")
                    onComplete(false, error.message as? String)
                }
        }
    }
    
    // ========================================
    // Making Moves
    // ========================================
    
    /**
     * Place a pin on the board.
     */
    fun placePin(
        row: Int,
        col: Int,
        color: String,
        isJoker: Boolean = false,
        originalColor: String? = null,
        handColor: String? = null,  // For jokers: the original hand color (for undo)
        forPlayerId: String? = null,  // Optional: specify which player is placing (defaults to current player)
        onComplete: (Boolean, String?) -> Unit
    ) {
        val roomId = currentRoomId
        val playerId = forPlayerId ?: currentPlayerId  // Use provided player ID or fall back to current
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null || playerId == null) {
            console.error("SharedBoardManager: Cannot place pin - roomId=$roomId, playerId=$playerId")
            onComplete(false, "Not in a game")
            return
        }
        
        val posKey = "${row}_${col}"
        console.log("SharedBoardManager: 📤 Placing pin at $posKey - $color (isJoker=$isJoker, handColor=$handColor, forPlayer=$playerId)")
        console.log("SharedBoardManager: Writing to games/$roomId/board/pins/$posKey")
        
        val pinData = json(
            "color" to color,
            "isDress" to false,
            "isJoker" to isJoker,
            "isReversed" to false,
            "originalColor" to originalColor,
            "handColor" to handColor,
            "playerId" to playerId,
            "moveIndex" to nextMoveIndex
        )
        
        // Use REST fallback for Discord/PlayGama or if database is null
        if (useRest || db == null) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for placePin")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                
                // 1. Place the pin
                val pinUrl = "${getRestBaseUrl()}/games/$roomId/board/pins/$posKey.json$authParam"
                window.fetch(pinUrl, json("method" to "PUT", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(pinData)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Pin placed successfully at $posKey")
                            
                            // 2. Remove pin from player's hand
                            val currentPlayer = cachedPlayers[playerId]
                            if (currentPlayer != null) {
                                val newHand = currentPlayer.hand.toMutableList()
                                // Remove the color that was placed (if not a joker, it's the color itself; if joker, use originalColor)
                                val colorToRemove = if (isJoker) originalColor ?: color else color
                                val indexToRemove = newHand.indexOf(colorToRemove)
                                if (indexToRemove >= 0) {
                                    newHand.removeAt(indexToRemove)
                                    console.log("SharedBoardManager: ✅ Removed $colorToRemove from hand, remaining: $newHand")
                                    
                                    // Update player's hand in Firebase via REST
                                    val handArray = js("[]")
                                    newHand.forEachIndexed { i, c -> handArray[i] = c }
                                    val handUrl = "${getRestBaseUrl()}/games/$roomId/players/$playerId/hand.json$authParam"
                                    window.fetch(handUrl, json("method" to "PUT", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(handArray)).asDynamic())
                                } else {
                                    console.warn("SharedBoardManager: ⚠️ Color $colorToRemove not found in hand $newHand")
                                }
                            }
                            
                            // 3. Handle Joker update if needed (Reuse token)
                            if (isJoker) {
                                val currentJokers = cachedPlayers[playerId]?.jokersUsed ?: 0
                                val jokerUrl = "${getRestBaseUrl()}/games/$roomId/players/$playerId/jokersUsed.json$authParam"
                                window.fetch(jokerUrl, json("method" to "PUT", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(currentJokers + 1)).asDynamic())
                            }
                            
                            // 4. Save to history (Reuse token)
                            val historyPinJson = json(
                                "color" to color,
                                "isJoker" to isJoker,
                                "originalColor" to originalColor,
                                "isReversed" to false,
                                "isJokerTurnedBlack" to false
                            )
                            saveMoveToHistory(roomId, playerId, row, col, historyPinJson, nextMoveIndex, token)
                            
                            nextMoveIndex++
                            onComplete(true, null)
                        } else {
                            onComplete(false, "REST error: ${response.status}")
                        }
                    }
                    .catch { error ->
                        onComplete(false, error.toString())
                    }
            }
        } else if (db != null) {
            // Standard Firebase SDK path for browsers
            val pinRef = db.ref("games/$roomId/board/pins/$posKey")
            pinRef.set(pinData)
                .then {
                    console.log("SharedBoardManager: ✅ Pin placed successfully at $posKey")
                    
                    // Update player's hand - remove the placed pin
                    val currentPlayer = cachedPlayers[playerId]
                    if (currentPlayer != null) {
                        val newHand = currentPlayer.hand.toMutableList()
                        val colorToRemove = if (isJoker) originalColor ?: color else color
                        val indexToRemove = newHand.indexOf(colorToRemove)
                        if (indexToRemove >= 0) {
                            newHand.removeAt(indexToRemove)
                            console.log("SharedBoardManager: ✅ Removed $colorToRemove from hand, remaining: $newHand")
                            
                            // Update hand in Firebase
                            val handArray = js("[]")
                            newHand.forEachIndexed { i, c -> handArray[i] = c }
                            db.ref("games/$roomId/players/$playerId/hand").set(handArray)
                        } else {
                            console.warn("SharedBoardManager: ⚠️ Color $colorToRemove not found in hand $newHand")
                        }
                    }
                    
                    // Update Joker counter
                    if (isJoker) {
                        db.ref("games/$roomId/players/$playerId/jokersUsed").transaction { currentValue: dynamic ->
                            val current = (currentValue as? Number)?.toInt() ?: 0
                            current + 1
                        }
                    }
                    
                    // Save to history
                    val historyPinJson = json(
                        "color" to color,
                        "isJoker" to isJoker,
                        "originalColor" to originalColor,
                        "isReversed" to false,
                        "isJokerTurnedBlack" to false
                    )
                    saveMoveToHistory(roomId, playerId, row, col, historyPinJson, nextMoveIndex)
                    
                    nextMoveIndex++
                    onComplete(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to place pin: ${error.message}")
                    onComplete(false, error.message as? String)
                }
        } else {
            // Database is null and we're not using REST - this shouldn't happen
            console.error("SharedBoardManager: ❌ Cannot place pin - no database and REST is not enabled")
            onComplete(false, "No database connection")
        }
    }
    
    /**
     * Place a pin on the board (convenience overload accepting Pin object).
     */
    fun placePin(
        row: Int,
        col: Int,
        pin: co.uk.paxes.pinpoint.model.Pin,
        forPlayerId: String? = null,  // Optional: specify which player is placing
        onComplete: (Boolean, String?) -> Unit
    ) {
        placePin(
            row = row,
            col = col,
            color = pin.getEffectiveColor().name,
            isJoker = pin.isJoker,
            originalColor = if (pin.isJoker) pin.color.name else null,
            forPlayerId = forPlayerId,
            onComplete = onComplete
        )
    }
    
    /**
     * Remove a pin from the board (for undo).
     * Returns false if undo limit reached.
     */
    fun removePin(
        row: Int,
        col: Int,
        currentUndosUsed: Int,
        onComplete: (Boolean, String?) -> Unit
    ) {
        val roomId = currentRoomId
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null) {
            onComplete(false, "Not in a game")
            return
        }
        
        // Check undo limit
        if (currentUndosUsed >= currentSettings.maxUndosPerHand) {
            console.log("SharedBoardManager: ⚠️ Undo limit reached (${currentUndosUsed}/${currentSettings.maxUndosPerHand})")
            onComplete(false, "Undo limit reached")
            return
        }
        
        val posKey = "${row}_${col}"
        console.log("SharedBoardManager: 🔄 Removing pin at $posKey for undo")
        
        val path = "games/$roomId/board/pins/$posKey"
        if (useRest || db == null) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for removePin")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/$path.json$authParam"
                kotlinx.browser.window.fetch(url, js("({ method: 'DELETE' })"))
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Pin removed via REST at $posKey")
                            if (nextMoveIndex > 0) {
                                nextMoveIndex--
                                removeMoveFromHistory(roomId, nextMoveIndex)
                            }
                            onComplete(true, null)
                        } else {
                            console.error("SharedBoardManager: ❌ REST remove failed: ${response.status}")
                            onComplete(false, "REST error ${response.status}")
                        }
                    }
                    .catch { error ->
                        console.error("SharedBoardManager: ❌ REST remove error: $error")
                        onComplete(false, error.toString())
                    }
            }
        } else if (db != null) {
            val pinRef = db.ref(path)
            pinRef.remove()
                .then {
                    console.log("SharedBoardManager: ✅ Pin removed via Firebase at $posKey")
                    if (nextMoveIndex > 0) {
                        nextMoveIndex--
                        removeMoveFromHistory(roomId, nextMoveIndex)
                    }
                    onComplete(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to remove pin: ${error.message}")
                    onComplete(false, error.message as? String)
                }
        } else {
            onComplete(false, "No database connection")
        }
    }
    
    /**
     * Update a pin's state (e.g., mark as reversed).
     */
    fun updatePin(
        row: Int,
        col: Int,
        updates: Map<String, Any?>,
        onComplete: (Boolean, String?) -> Unit
    ) {
        val roomId = currentRoomId
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null) {
            onComplete(false, "Not in a game")
            return
        }
        
        val posKey = "${row}_${col}"
        console.log("SharedBoardManager: Updating pin at $posKey with updates: $updates")
        
        val updateObj = js("{}")
        updates.forEach { (key, value) ->
            updateObj[key] = value
        }
        
        val path = "games/$roomId/board/pins/$posKey"
        if (useRest || db == null) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for updatePin")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/$path.json$authParam"
                kotlinx.browser.window.fetch(url, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(updateObj)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Pin updated via REST at $posKey")
                            onComplete(true, null)
                        } else {
                            console.error("SharedBoardManager: ❌ REST update failed: ${response.status}")
                            onComplete(false, "REST error ${response.status}")
                        }
                    }
                    .catch { error ->
                        console.error("SharedBoardManager: ❌ REST update error: $error")
                        onComplete(false, error.toString())
                    }
            }
        } else if (db != null) {
            val pinRef = db.ref(path)
            pinRef.update(updateObj)
                .then {
                    console.log("SharedBoardManager: ✅ Pin updated via Firebase at $posKey")
                    onComplete(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to update pin: ${error.message}")
                    onComplete(false, error.message as? String)
                }
        } else {
            onComplete(false, "No database connection")
        }
    }
    
    /**
     * Update multiple pins at once (for line reversal).
     */
    fun updateMultiplePins(
        pinUpdates: List<Triple<Int, Int, Map<String, Any?>>>,  // (row, col, updates)
        onComplete: (Boolean, String?) -> Unit
    ) {
        val roomId = currentRoomId
        val db = database
        
        if (roomId == null || db == null) {
            onComplete(false, "Not in a game")
            return
        }
        
        val updates = js("{}")
        pinUpdates.forEach { (row, col, pinUpdates) ->
            val posKey = "${row}_${col}"
            pinUpdates.forEach { (key, value) ->
                updates["board/pins/$posKey/$key"] = value
            }
        }
        
        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for updateMultiplePins")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/games/$roomId.json$authParam"
                kotlinx.browser.window.fetch(url, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(updates)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Multiple pins updated via REST")
                            onComplete(true, null)
                        } else {
                            console.error("SharedBoardManager: ❌ REST multi-update failed: ${response.status}")
                            onComplete(false, "REST error ${response.status}")
                        }
                    }
                    .catch { error ->
                        console.error("SharedBoardManager: ❌ REST multi-update error: $error")
                        onComplete(false, error.toString())
                    }
            }
        } else {
            val gameRef = db.ref("games/$roomId")
            gameRef.update(updates)
                .then {
                    console.log("SharedBoardManager: Multiple pins updated successfully")
                    onComplete(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: Failed to update pins: ${error.message}")
                    onComplete(false, error.message as? String)
                }
        }
    }

    
    // ========================================
    // Game Meta Updates
    // ========================================
    
    /**
     * Update game metadata (current player, phase, etc.).
     */
    fun updateMeta(
        updates: Map<String, Any?>,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        val roomId = currentRoomId
        val playerId = currentPlayerId
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null || playerId == null) {
            onComplete?.invoke(false, "Not in a game")
            return
        }
        
        val updateObj = js("{}")
        updates.forEach { (key, value) ->
            updateObj[key] = value
        }
        updateObj["lastUpdated"] = Date.now()
        updateObj["lastUpdatedBy"] = playerId
        
        val path = "games/$roomId/meta"
        
        // Use REST fallback for Discord/PlayGama or if database is null
        if (useRest || db == null) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for meta update")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/$path.json$authParam"
                window.fetch(url, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(updateObj)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Meta updated via REST: $updates")
                            onComplete?.invoke(true, null)
                        } else {
                            onComplete?.invoke(false, "REST error: ${response.status}")
                        }
                    }
                    .catch { error ->
                        onComplete?.invoke(false, error.toString())
                    }
            }
        } else if (db != null) {
            val metaRef = db.ref(path)
            metaRef.update(updateObj)
                .then {
                    console.log("SharedBoardManager: ✅ Meta updated via Firebase: $updates")
                    onComplete?.invoke(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to update meta: ${error.message}")
                    onComplete?.invoke(false, error.message as? String)
                }
        } else {
            onComplete?.invoke(false, "No database connection")
        }
    }
    
    /**
     * Update player data (score, hand, etc.).
     */
    fun updatePlayer(
        playerId: String,
        updates: Map<String, Any?>,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        val roomId = currentRoomId
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null) {
            onComplete?.invoke(false, "Not in a game")
            return
        }
        
        val updateObj = js("{}")
        updates.forEach { (key, value) ->
            updateObj[key] = value
        }
        
        val path = "games/$roomId/players/$playerId"
        
        // Use REST fallback for Discord/PlayGama or if database is null
        if (useRest || db == null) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for player update: $updates")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/$path.json$authParam"
                window.fetch(url, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(updateObj)).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ Player $playerId updated via REST")
                            onComplete?.invoke(true, null)
                        } else {
                            onComplete?.invoke(false, "REST error: ${response.status}")
                        }
                    }
                    .catch { error ->
                        onComplete?.invoke(false, error.toString())
                    }
            }
        } else if (db != null) {
            val playerRef = db.ref(path)
            playerRef.update(updateObj)
                .then {
                    console.log("SharedBoardManager: ✅ Player $playerId updated via Firebase")
                    onComplete?.invoke(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Failed to update player: ${error.message}")
                    onComplete?.invoke(false, error.message as? String)
                }
        } else {
            onComplete?.invoke(false, "No database connection")
        }
    }
    
    /**
     * Update player's hand (remove a pin after placement).
     */
    fun removeFromHand(
        playerId: String,
        pinIndex: Int,
        currentHand: List<String>,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        val newHand = currentHand.toMutableList()
        if (pinIndex in newHand.indices) {
            newHand.removeAt(pinIndex)
        }
        
        // Create JS array for Firebase compatibility
        val handArray = js("[]")
        newHand.forEachIndexed { i, c ->
            handArray[i] = c
        }
        
        updatePlayer(playerId, mapOf("hand" to handArray), onComplete)
    }
    
    /**
     * Add a pin back to player's hand (for undo).
     */
    fun addToHand(
        playerId: String,
        color: String,
        currentHand: List<String>,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        val newHand = currentHand.toMutableList()
        newHand.add(color)
        
        // Create JS array for Firebase compatibility
        val handArray = js("[]")
        newHand.forEachIndexed { i, c ->
            handArray[i] = c
        }
        
        updatePlayer(playerId, mapOf("hand" to handArray), onComplete)
    }
    
    /**
     * Deal a new hand to a player.
     */
    fun dealNewHand(
        playerId: String,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        // Hand colors: one of each (RED, GREEN, PURPLE, YELLOW) - no BLACK, no duplicates
        val handColors = listOf("RED", "GREEN", "PURPLE", "YELLOW")
        val newHand = handColors.shuffled()
        
        // Create JS array for Firebase compatibility
        val handArray = js("[]")
        newHand.forEachIndexed { i, color ->
            handArray[i] = color
        }
        
        updatePlayer(playerId, mapOf("hand" to handArray), onComplete)
    }
    
    /**
     * Atomically pass turn to next player AND deal them a new hand.
     * This prevents race conditions where meta updates before hand.
     */
    fun passTurnWithNewHand(
        nextPlayerId: String,
        nextPlayerIndex: Int,
        newHandCount: Int,
        onComplete: ((Boolean, String?) -> Unit)? = null
    ) {
        val roomId = currentRoomId
        val playerId = currentPlayerId
        val db = database
        
        // CRITICAL: In PlayGama/Discord, db will be null - that's OK, we use REST
        if (roomId == null || playerId == null) {
            console.error("SharedBoardManager: passTurnWithNewHand FAILED - roomId=$roomId, playerId=$playerId")
            onComplete?.invoke(false, "Not in a game")
            return
        }
        
        // Generate new hand - one of each color, shuffled
        val handColors = listOf("RED", "GREEN", "PURPLE", "YELLOW")
        val newHand = handColors.shuffled()
        
        console.log("SharedBoardManager: 🔄 Atomic turn pass - nextPlayer=$nextPlayerId, newHand=$newHand")
        console.log("SharedBoardManager: 🔄 Writing to games/$roomId/players/$nextPlayerId/hand")
        
        // Build atomic update object that updates BOTH player hand AND meta
        val updates = js("{}")
        
        // Update player's hand - use JS array directly for Firebase compatibility
        val handArray = js("[]")
        newHand.forEachIndexed { index, color ->
            handArray[index] = color
        }
        updates["players/$nextPlayerId/hand"] = handArray
        
        // Update meta
        updates["meta/currentPlayerId"] = nextPlayerId
        updates["meta/currentPlayerIndex"] = nextPlayerIndex
        updates["meta/handCount"] = newHandCount
        updates["meta/currentHandScore"] = 0
        updates["meta/lastPinScore"] = 0  // Reset pin score for new turn
        updates["meta/scoringPositions"] = emptyArray<String>()  // Clear scoring highlights
        updates["meta/gamePhase"] = "PLAYING"
        updates["meta/pauseEndTime"] = null
        updates["meta/pauseStartedBy"] = null
        updates["meta/undosUsedThisHand"] = 0
        updates["meta/lastUpdated"] = Date.now()
        updates["meta/lastUpdatedBy"] = playerId
        
        console.log("SharedBoardManager: 🔄 Atomic update prepared for $nextPlayerId")
        
        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for passTurnWithNewHand")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val payload = JSON.stringify(updates)
                console.log("SharedBoardManager: 📤 Sending REST PATCH turn pass...")
                
                val url = "${getRestBaseUrl()}/games/$roomId.json$authParam"
                window.fetch(url, json("method" to "PATCH", "body" to payload).asDynamic())
                    .then { response ->
                        if (response.ok) {
                            console.log("SharedBoardManager: ✅ REST turn pass SUCCESS")
                            onComplete?.invoke(true, null)
                        } else {
                            console.error("SharedBoardManager: ❌ REST turn pass FAILED with status ${response.status}")
                            response.text().then { text ->
                                console.error("SharedBoardManager: ❌ Error details: $text")
                            }
                            onComplete?.invoke(false, "HTTP ${response.status}")
                        }
                    }
                    .catch { err ->
                        console.error("SharedBoardManager: ❌ REST turn pass EXCEPTION: $err")
                        onComplete?.invoke(false, err.toString())
                    }
            }
        } else if (db != null) {
            val gameRef = db.ref("games/$roomId")
            gameRef.update(updates)
                .then {
                    console.log("SharedBoardManager: ✅ Atomic turn pass successful via Firebase - $nextPlayerId now has hand: $newHand")
                    onComplete?.invoke(true, null)
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: ❌ Atomic turn pass failed: ${error.message}")
                    onComplete?.invoke(false, error.message as? String)
                }
        } else {
            onComplete?.invoke(false, "No database connection")
        }
    }
    
    // ========================================
    // Firebase Listeners
    // ========================================
    
    private fun subscribeToBoard(roomId: String) {
        val db = database
        if (db == null) {
            console.error("SharedBoardManager: Cannot subscribe to board - database is null!")
            return
        }
        
        console.log("SharedBoardManager: Subscribing to board at games/$roomId/board/pins")
        
        val boardRef = db.ref("games/$roomId/board/pins")
        boardListener = boardRef.on("value", { snapshot: dynamic ->
            try {
                console.log("SharedBoardManager: 📥 Board snapshot received!")
                val pins = mutableMapOf<String, SharedPin>()
                val data = snapshot.`val`()
                
                if (data != null) {
                    val keys = js("Object.keys")(data) as Array<String>
                    console.log("SharedBoardManager: Parsing ${keys.size} pin keys: ${keys.joinToString()}")
                    for (key in keys) {
                        val pinData = data[key]
                        if (pinData != null) {
                            val pin = SharedPin(
                                color = (pinData.color as? String) ?: "RED",
                                isDress = (pinData.isDress as? Boolean) ?: false,
                                isJoker = (pinData.isJoker as? Boolean) ?: false,
                                isReversed = (pinData.isReversed as? Boolean) ?: false,
                                originalColor = pinData.originalColor as? String,
                                handColor = pinData.handColor as? String,
                                playerId = pinData.playerId as? String,
                                moveIndex = (pinData.moveIndex as? Number)?.toInt() ?: -1
                            )
                            pins[key] = pin
                            console.log("SharedBoardManager: Pin at $key: ${pin.color} (isDress=${pin.isDress})")
                        }
                    }
                    
                    // Update nextMoveIndex based on highest moveIndex found
                    val maxIndex = pins.values.maxOfOrNull { it.moveIndex } ?: -1
                    nextMoveIndex = maxIndex + 1
                } else {
                    console.log("SharedBoardManager: Board data is null/empty")
                }
                
                console.log("SharedBoardManager: Board updated - ${pins.size} pins total")
                val callback = onBoardChanged
                if (callback != null) {
                    console.log("SharedBoardManager: Invoking onBoardChanged callback")
                    callback.invoke(pins)
                } else {
                    console.error("SharedBoardManager: ⚠️ onBoardChanged callback is NULL!")
                }
            } catch (e: Exception) {
                console.error("SharedBoardManager: Error parsing board: ${e.message}")
            }
        }, { error: dynamic ->
            console.error("SharedBoardManager: Board listener error: ${error.message}")
            onError?.invoke(error.message as? String ?: "Board listener error")
        })
    }
    
    /**
     * Fetch the current board state once (no listener).
     * Used by AI to get fresh board data before move evaluation.
     */
    fun fetchBoardOnce(callback: (Map<String, SharedPin>) -> Unit) {
        val roomId = currentRoomId
        val db = database
        
        if (roomId == null) {
            console.error("SharedBoardManager: Cannot fetch board - roomId is null!")
            callback(emptyMap())
            return
        }

        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST fallback to fetch board once")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val url = "${getRestBaseUrl()}/games/$roomId/board/pins.json$authParam"
                
                window.fetch(url)
                    .then { response -> 
                        if (response.ok) response.json() else null
                    }
                    .then { data: dynamic ->
                        val pins = mutableMapOf<String, SharedPin>()
                        if (data != null) {
                            val keys = js("Object.keys")(data) as Array<String>
                            for (key in keys) {
                                val pinData = data[key]
                                if (pinData != null) {
                                    val pin = SharedPin(
                                        color = (pinData.color as? String) ?: "RED",
                                        isDress = (pinData.isDress as? Boolean) ?: false,
                                        isJoker = (pinData.isJoker as? Boolean) ?: false,
                                        isReversed = (pinData.isReversed as? Boolean) ?: false,
                                        originalColor = pinData.originalColor as? String,
                                        handColor = pinData.handColor as? String,
                                        playerId = pinData.playerId as? String,
                                        moveIndex = (pinData.moveIndex as? Number)?.toInt() ?: -1
                                    )
                                    pins[key] = pin
                                }
                            }
                        }
                        console.log("SharedBoardManager: ✅ REST Board fetch success: ${pins.size} pins")
                        callback(pins)
                    }
                    .catch { err ->
                        console.error("SharedBoardManager: ❌ REST Board fetch failed: $err")
                        callback(emptyMap())
                    }
            }
            return
        }
        
        if (db == null) {
            console.error("SharedBoardManager: Cannot fetch board - database is null!")
            callback(emptyMap())
            return
        }
        
        console.log("SharedBoardManager: 🔍 Fetching board once from games/$roomId/board/pins")
        val boardRef = db.ref("games/$roomId/board/pins")
        boardRef.once("value").then({ snapshot: dynamic ->
            try {
                console.log("SharedBoardManager: 🔍 Board snapshot fetched!")
                val pins = mutableMapOf<String, SharedPin>()
                val data = snapshot.`val`()
                
                if (data != null) {
                    val keys = js("Object.keys")(data) as Array<String>
                    console.log("SharedBoardManager: 🔍 Parsing ${keys.size} pin keys")
                    for (key in keys) {
                        val pinData = data[key]
                        if (pinData != null) {
                            val pin = SharedPin(
                                color = (pinData.color as? String) ?: "RED",
                                isDress = (pinData.isDress as? Boolean) ?: false,
                                isJoker = (pinData.isJoker as? Boolean) ?: false,
                                isReversed = (pinData.isReversed as? Boolean) ?: false,
                                originalColor = pinData.originalColor as? String,
                                handColor = pinData.handColor as? String,
                                playerId = pinData.playerId as? String,
                                moveIndex = (pinData.moveIndex as? Number)?.toInt() ?: -1
                            )
                            pins[key] = pin
                        }
                    }
                } else {
                    console.log("SharedBoardManager: 🔍 Board data is null/empty")
                }
                
                console.log("SharedBoardManager: 🔍 Fetched ${pins.size} pins")
                callback(pins)
            } catch (e: Exception) {
                console.error("SharedBoardManager: 🔍 Error fetching board: ${e.message}")
                callback(emptyMap())
            }
        }).catch({ error: dynamic ->
            console.error("SharedBoardManager: 🔍 Board fetch error: ${error.message}")
            callback(emptyMap())
        })
    }
    
    private fun subscribeToMeta(roomId: String) {
        val db = database
        if (db == null) {
            console.error("SharedBoardManager: Cannot subscribe to meta - database is null!")
            return
        }
        
        console.log("SharedBoardManager: Subscribing to meta at games/$roomId/meta")
        
        val metaRef = db.ref("games/$roomId/meta")
        metaListener = metaRef.on("value", { snapshot: dynamic ->
            try {
                console.log("SharedBoardManager: 📥 Meta snapshot received!")
                val data = snapshot.`val`()
                
                if (data != null) {
                    // Add detailed logging of the raw data
                    console.log("SharedBoardManager: Raw meta data from Firebase: ${JSON.stringify(data)}")
                    
                    // Parse scoring positions - robustly handle Array, Object, or String (new format)
                    val rawPositions = data.scoringPositions
                    val rawType = js("typeof rawPositions") as String
                    console.log("SharedBoardManager: 📥 Raw scoringPositions from Firebase: $rawPositions (type: $rawType)")
                    
                    val scoringPositions: List<String> = when {
                        rawPositions == null || rawPositions == undefined -> {
                            console.log("SharedBoardManager: scoringPositions is null/undefined")
                            emptyList()
                        }
                        // Handle comma-separated string - use JS typeof for reliable type check
                        rawType == "string" -> {
                            val str = rawPositions.toString()
                            console.log("SharedBoardManager: Parsing string positions: '$str'")
                            if (str.trim().isEmpty()) {
                                emptyList()
                            } else {
                                val list = mutableListOf<String>()
                                val parts = str.split(",")
                                for (part in parts) {
                                    val trimmed = part.trim()
                                    if (trimmed.isNotEmpty()) list.add(trimmed)
                                }
                                console.log("SharedBoardManager: Parsed ${list.size} positions from string: $list")
                                list
                            }
                        }
                        // Handle JS Array
                        js("Array.isArray(rawPositions)") as Boolean -> {
                            console.log("SharedBoardManager: Parsing JS Array positions")
                            try {
                                val list = mutableListOf<String>()
                                val length = rawPositions.length as Int
                                for (i in 0 until length) {
                                    val item = rawPositions[i]
                                    if (item != null) {
                                        list.add(item.toString())
                                    }
                                }
                                console.log("SharedBoardManager: Parsed ${list.size} positions from array: $list")
                                list
                            } catch (e: Exception) {
                                console.error("SharedBoardManager: Error parsing array positions: $e")
                                emptyList()
                            }
                        }
                        // Handle Object-as-Array (Firebase fallback)
                        rawType == "object" -> {
                            console.log("SharedBoardManager: Parsing Object positions")
                            try {
                                val list = mutableListOf<String>()
                                val keys = js("Object.keys(rawPositions)") as Array<String>
                                for (key in keys) {
                                    val item = rawPositions[key]
                                    if (item != null) {
                                        list.add(item.toString())
                                    }
                                }
                                console.log("SharedBoardManager: Parsed ${list.size} positions from object: $list")
                                list
                            } catch (e: Exception) {
                                console.error("SharedBoardManager: Error parsing object positions: $e")
                                emptyList()
                            }
                        }
                        else -> {
                            console.log("SharedBoardManager: Unknown scoringPositions type: $rawType")
                            emptyList()
                        }
                    }
                    
                    console.log("SharedBoardManager: 📥 Final parsed scoringPositions: $scoringPositions (count: ${scoringPositions.size})")
                    
                    val meta = GameMeta(
                        currentPlayerId = (data.currentPlayerId as? String) ?: "",
                        currentPlayerIndex = (data.currentPlayerIndex as? Number)?.toInt() ?: 0,
                        gamePhase = (data.gamePhase as? String) ?: "PLAYING",
                        turnNumber = (data.turnNumber as? Number)?.toInt() ?: 1,
                        handCount = (data.handCount as? Number)?.toInt() ?: 0,
                        undosUsedThisHand = (data.undosUsedThisHand as? Number)?.toInt() ?: 0,
                        lastPinScore = (data.lastPinScore as? Number)?.toInt() ?: 0,
                        currentHandScore = (data.currentHandScore as? Number)?.toInt() ?: 0,
                        scoringPositions = scoringPositions,
                        pauseEndTime = (data.pauseEndTime as? Number)?.toLong(),
                        pauseStartedBy = data.pauseStartedBy as? String,
                        lastUpdated = (data.lastUpdated as? Number)?.toLong() ?: 0,
                        lastUpdatedBy = data.lastUpdatedBy as? String
                    )
                    
                    console.log("SharedBoardManager: Meta updated - phase=${meta.gamePhase}, currentPlayer=${meta.currentPlayerId}, scoringPos=${meta.scoringPositions.size}")
                    onMetaChanged?.invoke(meta)
                } else {
                    console.log("SharedBoardManager: Meta data is null/empty")
                }
            } catch (e: Exception) {
                console.error("SharedBoardManager: Error parsing meta: ${e.message}")
            }
        }, { error: dynamic ->
            console.error("SharedBoardManager: Meta listener error: ${error.message}")
            onError?.invoke(error.message as? String ?: "Meta listener error")
        })
    }
    
    private fun subscribeToPlayers(roomId: String) {
        val db = database
        if (db == null) {
            console.error("SharedBoardManager: Cannot subscribe to players - database is null!")
            return
        }
        
        console.log("SharedBoardManager: Subscribing to players at games/$roomId/players")
        
        val playersRef = db.ref("games/$roomId/players")
        playersListener = playersRef.on("value", { snapshot: dynamic ->
            try {
                console.log("SharedBoardManager: 📥 Players snapshot received!")
                val players = mutableMapOf<String, SharedPlayer>()
                val data = snapshot.`val`()
                
                if (data != null) {
                    val keys = js("Object.keys")(data) as Array<String>
                    console.log("SharedBoardManager: Parsing ${keys.size} player keys: ${keys.joinToString()}")
                    for (playerId in keys) {
                        val playerData = data[playerId]
                        if (playerData != null) {
                            // Parse hand array
                            val handArray = playerData.hand
                            val hand = if (handArray != null) {
                                try {
                                    val list = mutableListOf<String>()
                                    
                                    when {
                                        // Case 1: It's a string "RED,GREEN"
                                        handArray is String -> {
                                            val str = handArray as String
                                            if (str.isNotEmpty()) {
                                                 str.split(",").forEach { if (it.isNotEmpty()) list.add(it) }
                                            }
                                        }
                                        // Case 2: It's an Array
                                        js("Array.isArray(handArray)") as Boolean -> {
                                            val arr = handArray as Array<dynamic>
                                            for (item in arr) {
                                                if (item != null) list.add(item.toString())
                                            }
                                        }
                                        // Case 3: It's an Object (Firebase array-like object)
                                        else -> {
                                            val keys2 = js("Object.keys(handArray)") as Array<String>
                                            val indices = mutableListOf<Int>()
                                            
                                            // Handle cases where keys might be strings but numeric content
                                            for (k in keys2) {
                                                val i = k.toIntOrNull()
                                                if (i != null) {
                                                    indices.add(i)
                                                } else {
                                                    // Fallback: if keys aren't numeric, just treat as list of values
                                                    // This handles unexpected object structures
                                                    val item = (handArray.unsafeCast<dynamic>())[k]
                                                    if (item != null) list.add(item.toString())
                                                }
                                            }
                                            
                                            // If we found numeric indices, sort and use them
                                            if (indices.isNotEmpty()) {
                                                indices.sort()
                                                // Clear any non-numeric additions to avoid duplicates if mixed
                                                if (list.isNotEmpty()) list.clear()
                                                
                                                for (i in indices) {
                                                    val dynHand = handArray.unsafeCast<dynamic>()
                                                    val item = dynHand[i.toString()]
                                                    if (item != null) list.add(item.toString())
                                                }
                                            }
                                        }
                                    }
                                    
                                    if (list.isEmpty() && handArray != null) {
                                        val str = try { JSON.stringify(handArray) } catch(e: Exception) { "unstringifiable" }
                                        console.warn("SharedBoardManager: Parsed empty hand from non-null data: $str")
                                    }
                                    
                                    list
                                } catch (e: Exception) {
                                    console.error("SharedBoardManager: Error parsing hand for $playerId: ${e.message}")
                                    emptyList()
                                }
                            } else {
                                emptyList()
                            }
                            
                            val player = SharedPlayer(
                                name = (playerData.name as? String) ?: "Unknown",
                                score = (playerData.score as? Number)?.toInt() ?: 0,
                                isHost = (playerData.isHost as? Boolean) ?: false,
                                isAI = (playerData.isAI as? Boolean) ?: false,  // Load AI flag
                                aiDifficulty = playerData.aiDifficulty as? String,  // Load AI difficulty
                                hand = hand,
                                jokersUsed = (playerData.jokersUsed as? Number)?.toInt() ?: 0
                            )
                            players[playerId] = player
                            console.log("SharedBoardManager: Player $playerId: ${player.name}, hand=${hand.joinToString()}")
                        }
                    }
                } else {
                    console.log("SharedBoardManager: Players data is null/empty")
                }
                
                cachedPlayers = players
                console.log("SharedBoardManager: Players updated - ${players.size} players")
                val callback = onPlayersChanged
                if (callback != null) {
                    console.log("SharedBoardManager: Invoking onPlayersChanged callback")
                    callback.invoke(players)
                } else {
                    console.error("SharedBoardManager: ⚠️ onPlayersChanged callback is NULL!")
                }
            } catch (e: Exception) {
                console.error("SharedBoardManager: Error parsing players: ${e.message}")
            }
        }, { error: dynamic ->
            console.error("SharedBoardManager: Players listener error: ${error.message}")
            onError?.invoke(error.message as? String ?: "Players listener error")
        })
    }
    
    private fun subscribeToSettings(roomId: String) {
        val db = database
        if (db == null) {
            console.error("SharedBoardManager: Cannot subscribe to settings - database is null!")
            return
        }
        
        console.log("SharedBoardManager: Subscribing to settings at games/$roomId/settings")
        
        val settingsRef = db.ref("games/$roomId/settings")
        settingsListener = settingsRef.on("value", { snapshot: dynamic ->
            try {
                console.log("SharedBoardManager: 📥 Settings snapshot received!")
                val data = snapshot.`val`()
                
                if (data != null) {
                    val settings = GameSettings(
                        maxUndosPerHand = (data.maxUndosPerHand as? Number)?.toInt() ?: 1
                    )
                    
                    currentSettings = settings
                    console.log("SharedBoardManager: Settings updated - maxUndosPerHand=${settings.maxUndosPerHand}")
                    onSettingsChanged?.invoke(settings)
                } else {
                    console.log("SharedBoardManager: Settings data is null/empty")
                }
            } catch (e: Exception) {
                console.error("SharedBoardManager: Error parsing settings: ${e.message}")
            }
        }, { error: dynamic ->
            console.error("SharedBoardManager: Settings listener error: ${error.message}")
            onError?.invoke(error.message as? String ?: "Settings listener error")
        })
    }
    
    // ========================================
    // Utility Functions
    // ========================================
    
    /**
     * Delete the entire game state (for cleanup).
     */
    fun deleteGame(roomId: String, onComplete: ((Boolean) -> Unit)? = null) {
        val db = database
        if (db == null) {
            onComplete?.invoke(false)
            return
        }
        val roomId = roomId
        
        if (useRest) {
            console.log("SharedBoardManager: 🌐 Using REST fallback for deleteGame")
            FirebaseRoomManager.getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val gameUrl = "${getRestBaseUrl()}/games/$roomId.json$authParam"
                val movesUrl = "${getRestBaseUrl()}/moves/$roomId.json$authParam"
                
                // Delete game first
                kotlinx.browser.window.fetch(gameUrl, js("({ method: 'DELETE' })"))
                    .then { response ->
                        if (response.ok) {
                            // Delete moves
                            kotlinx.browser.window.fetch(movesUrl, js("({ method: 'DELETE' })"))
                                .then {
                                    console.log("SharedBoardManager: ✅ Game $roomId and moves history deleted via REST")
                                    onComplete?.invoke(true)
                                }
                                .catch {
                                    console.error("SharedBoardManager: ❌ Failed to delete moves via REST")
                                    onComplete?.invoke(true) // Game deleted, so return true
                                }
                        } else {
                            console.error("SharedBoardManager: ❌ REST delete game failed: ${response.status}")
                            onComplete?.invoke(false)
                        }
                    }
                    .catch { error ->
                        console.error("SharedBoardManager: ❌ REST delete game error: $error")
                        onComplete?.invoke(false)
                    }
            }
        } else {
            val gameRef = db.ref("games/$roomId")
            gameRef.remove()
                .then {
                    // Also clear moves history
                    db.ref("moves/$roomId").remove()
                        .then {
                            console.log("SharedBoardManager: Game $roomId and moves history deleted")
                            onComplete?.invoke(true)
                        }
                        .catch { error: dynamic ->
                            console.error("SharedBoardManager: Failed to delete moves history: ${error.message}")
                            // Still return true as game was deleted
                            onComplete?.invoke(true)
                        }
                }
                .catch { error: dynamic ->
                    console.error("SharedBoardManager: Failed to delete game: ${error.message}")
                    onComplete?.invoke(false)
                }
        }
    }
    
    /**
     * Get current room ID.
     */
    fun getCurrentRoomId(): String? = currentRoomId
    
    /**
     * Get current player ID.
     */
    fun getCurrentPlayerId(): String? = currentPlayerId
    
    /**
     * Check if current player is host.
     */
    fun isCurrentPlayerHost(): Boolean = isHost
    
    /**
     * Get current settings.
     */
    fun getCurrentSettings(): GameSettings = currentSettings
    
    /**
     * Get max undos per hand from current settings.
     */
    fun getMaxUndosPerHand(): Int = currentSettings.maxUndosPerHand
    
    /**
     * Check if undo is allowed.
     */
    fun canUndo(currentUndosUsed: Int): Boolean {
        return currentUndosUsed < currentSettings.maxUndosPerHand
    }
    
    // ========================================
    // Move History Table (Android Compatibility)
    // ========================================

    /**
     * Save move to the moves history table (Android compatibility).
     */
    private fun saveMoveToHistory(roomId: String, playerId: String, row: Int, col: Int, pinJson: dynamic, moveIndex: Int, authToken: String? = null) {
        val db = database ?: return
        val timestamp = Date.now().toLong()
        
        // Android MoveEvent uses pin object directly
        val androidMovePayload = json(
            "row" to row,
            "col" to col,
            "pin" to pinJson
        )
        
        val eventEnvelope = json(
            "playerId" to playerId,
            "type" to "PLAYER_MOVE",
            "timestamp" to timestamp,
            "payload" to JSON.stringify(androidMovePayload)
        )
        
        console.log("SharedBoardManager: Saving move $moveIndex to moves/$roomId")
        
        if (useRest) {
            val sendWithToken = { token: String? ->
                val authParam = if (token != null) "?auth=" + token else ""
                // RELOCATION: Use games/$roomId/history instead of moves/$roomId to ensure we have write permissions
                val url = "${getRestBaseUrl()}/games/" + roomId + "/history/" + moveIndex + ".json" + authParam
                window.fetch(url, json("method" to "PATCH", "headers" to json("Content-Type" to "application/json"), "body" to JSON.stringify(eventEnvelope)).asDynamic())
            }
            
            if (authToken != null) {
                sendWithToken(authToken)
            } else {
                FirebaseRoomManager.getAuthToken { token -> sendWithToken(token) }
            }
        } else {
            db.ref("moves/$roomId/$moveIndex").set(eventEnvelope)
        }
    }

    /**
     * Remove move from the moves history table (for undo).
     */
    private fun removeMoveFromHistory(roomId: String, moveIndex: Int, authToken: String? = null) {
        val db = database ?: return
        console.log("SharedBoardManager: Removing move $moveIndex from moves/$roomId")
        
        if (useRest) {
            val removeWithToken = { token: String? ->
                val authParam = if (token != null) "?auth=" + token else ""
                // RELOCATION: Use games/$roomId/history instead of moves/$roomId
                val url = "${getRestBaseUrl()}/games/" + roomId + "/history/" + moveIndex + ".json" + authParam
                window.fetch(url, js("({ method: 'DELETE' })"))
            }
            
            if (authToken != null) {
                removeWithToken(authToken)
            } else {
                FirebaseRoomManager.getAuthToken { token -> removeWithToken(token) }
            }
        } else {
            db.ref("moves/$roomId/$moveIndex").remove()
        }
    }
}
